import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Select, Input, Tooltip, Icon, Button, Tag } from "antd";
import GLCodeSelect from "../../common/SelectDropdowns/GLCodeSelect";
import { DriverSelect } from "../../common/SelectDropdowns/BaseSelect";
import I18n from "../../../common/I18n";
import _ from "lodash";
import { saveManualBillingAccessorial } from "../../../api/Accessorials";
import { renderAlertMessage } from "../../../helpers/common";

const REQUIRED_FIELD_MESSAGES = {
  accessorial_code: "Can't be empty",
  location_id: "Can't be empty",
  gl_code: "Can't be empty",
  invoice_value: "Can't be empty",
};
const newLineTemplate = orderDetails => ({
  tempKey: `new-${Date.now()}`,
  accessorial_code: "",
  invoice_name: "",
  invoice_value: 0,
  invoice_quantity: 1,
  isNew: true,
  invoice_type: "Accessorial",
  order_item_group_id: null,
  location_id: (orderDetails?.all_locations && orderDetails.all_locations[0]?.location_id) || "",
  // location_type: (orderDetails && orderDetails.location_type) || "",
  order_id: (orderDetails && orderDetails.order_id) || "",
  gl_code: "",
  is_modified: false,
  driver_pay_params: [],
  account_accessorial_id: null,
  component_params: [],
  standard_code: "",
});

const getLineErrors = line => {
  const errors = {};

  if (!String(line.accessorial_code || "").trim()) {
    errors.accessorial_code = REQUIRED_FIELD_MESSAGES.accessorial_code;
  }

  if (!line.location_id) {
    errors.location_id = REQUIRED_FIELD_MESSAGES.location_id;
  }

  if (!String(line.gl_code || "").trim()) {
    errors.gl_code = REQUIRED_FIELD_MESSAGES.gl_code;
  }

  const val = line.invoice_value;
  if (val === "" || val === null || val === undefined) {
    errors.invoice_value = REQUIRED_FIELD_MESSAGES.invoice_value;
  }

  return errors;
};

const hasIncompleteLine = (chargeLine, editable) =>
  chargeLine.some(line => {
    if (!(line.isNew || editable)) return false;

    return Object.keys(getLineErrors(line)).length > 0;
  });

const getOrdinal = n => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const AccessorialChargeSection = ({
  section,
  onAddAccessorialLine = () => {},
  onEditAccessorialLine = () => {},
  onDeleteAccessorialLine = () => {},
  onValidationChange = () => {},
  editable = false,
  orderDetails = {},
  groupGlByWarehouseSetting = false,
  glCodesList = [],
  accessorialList = [],
  displayComponents = [],
  orgComponents = [],
  currentOrg = {},
  drivers = [],
}) => {
  const [chargeLine, setChargeLine] = useState(section.lines || []);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [currentAccCost, setCurrentAccCost] = useState(0);

  useEffect(() => {
    setChargeLine(
      (section.lines || []).map(line => ({
        ...line,
        tempKey: line.tempKey || _.uniqueId("tmp-"),
        invoice_type: line.invoice_type || "Accessorial",
        old_location_id: line.old_location_id || line.location_id,
        old_location_type: line.old_location_type || line.location_type,
      }))
    );
  }, [section.lines, section.key]);

  const getLineKey = (line, idx) => line.id || line.tempKey || `new-${idx}`;

  // Component param definitions for a given accessorial -> [{ component_code, name, key }]
  const getVisibleComponentParams = accessorial => {
    if (!accessorial || !Array.isArray(accessorial.components)) return [];
    const accComponentCodes = accessorial.components.map(c => c.component_code);

    return accComponentCodes.reduce((acc, code) => {
      if (!displayComponents.includes(code)) return acc;
      const orgComponent = orgComponents.find(oc => oc.code === code);
      if (!orgComponent || !Array.isArray(orgComponent.component_params)) return acc;

      orgComponent.component_params
        .filter(param => (param.visible_to || []).includes("DISPATCHER"))
        .forEach(param => {
          acc.push({ component_code: code, name: param.name, key: param.code });
        });

      return acc;
    }, []);
  };

  const getLineComponentDefs = line => {
    const selectedAcc = accessorialList.find(acc => acc.accessorial_code === line.accessorial_code);
    if (selectedAcc) {
      const defs = getVisibleComponentParams(selectedAcc);
      if (defs.length) return defs;
    }
    return (line.component_params || []).reduce((acc, cp) => {
      const orgComponent = orgComponents.find(oc => oc.code === cp.component_code);
      const paramDef = orgComponent?.component_params?.find(p => p.code === cp.accessorial_key);
      if (paramDef && (paramDef.visible_to || []).includes("DISPATCHER")) {
        acc.push({
          component_code: cp.component_code,
          name: paramDef.name,
          key: cp.accessorial_key,
        });
      }
      return acc;
    }, []);
  };

  const isZeroInvValue = line => {
    const val = line.invoice_value;
    if (val === "" || val === null || val === undefined) return false;
    return _.toNumber(val) === 0;
  };

  const isAtmpLine = line => {
    const selectedAcc = accessorialList.find(acc => acc.accessorial_code === line.accessorial_code);
    const standardCode = line.standard_code || selectedAcc?.standard_code;
    return standardCode === "ATMP";
  };

  const gridTemplateColumns = [
    "minmax(220px, 2.7fr)", // Description
    "minmax(70px, 0.85fr)", // Code
    "minmax(110px, 0.95fr)", // Order Type
    "minmax(110px, 0.9fr)", // GL Code
    "minmax(90px, 0.9fr)", // Amount
    editable ? "40px" : null, // Delete icon
  ]
    .filter(Boolean)
    .join(" ");

  const groupComponentParams = componentParams => {
    const grouped = {};
    (componentParams || []).forEach(cp => {
      if (!grouped[cp.component_code]) {
        grouped[cp.component_code] = { component_code: cp.component_code, component_values: [] };
      }
      grouped[cp.component_code].component_values.push({
        accessorial_key: cp.accessorial_key,
        accessorial_value: cp.accessorial_value,
      });
    });
    return Object.values(grouped);
  };

  // Builds the payload line matching the two required shapes:
  // existing/no-params -> flat fields only
  // new + expanded (has component params or ATMP driver attempts) -> flat fields + component_params + driver_pay_params
  const buildPayloadLine = (line, { isNew }) => {
    const base = {
      invoice_name: line.invoice_name,
      invoice_value: line.invoice_value,
      invoice_quantity: Number(line.invoice_quantity) || 1,
      invoice_type: line.invoice_type || "Accessorial",
      location_id: line.location_id,
      location_type: line.location_type,
      accessorial_code: line.accessorial_code,
      gl_code: line.gl_code,
      account_accessorial_id: line.account_accessorial_id || null,
    };

    if (!isNew) {
      if (line.id) base.invoice_line_id = line.id;
      base.old_location_id = line.old_location_id || line.location_id;
      base.old_location_type = line.old_location_type || line.location_type;
      base.is_modified = true;
    }

    const groupedParams = groupComponentParams(line.component_params);
    const attemptedDriverIds =
      (line.driver_pay_params && line.driver_pay_params.attempted_driver_ids) || [];

    if (groupedParams.length > 0) {
      base.component_params = groupedParams;
    }

    if (groupedParams.length > 0 || attemptedDriverIds.length > 0) {
      base.driver_pay_params = { attempted_driver_ids: attemptedDriverIds };
    }

    return base;
  };

  const updateLineField = (lineKey, field, value) => {
    setChargeLine(prev =>
      prev.map((line, idx) => {
        if (getLineKey(line, idx) !== lineKey) return line;
        const updated = { ...line, [field]: value };
        reportLine(updated, lineKey);
        return updated;
      })
    );
  };

  const reportLine = (line, lineKey) => {
    const isNew = !!line.isNew;
    const payloadLine = buildPayloadLine(line, { isNew });

    if (isNew) {
      onAddAccessorialLine(lineKey, payloadLine);
    } else {
      onEditAccessorialLine(lineKey, payloadLine);
    }
  };

  const handleAccessorialSelect = (lineKey, accessorialCode) => {
    const selectedAcc = accessorialList.find(acc => acc.accessorial_code === accessorialCode);

    setChargeLine(prev =>
      prev.map((line, idx) => {
        if (getLineKey(line, idx) !== lineKey) return line;
        const updated = {
          ...line,
          accessorial_code: accessorialCode,
          invoice_name: selectedAcc?.accessorial_name || "",
          gl_code: selectedAcc?.gl_code || "",
          account_accessorial_id: selectedAcc?.id || selectedAcc?._id || null,
          standard_code: selectedAcc?.standard_code || "",
          component_params: [],
          driver_pay_params: { attempted_driver_ids: [] },
        };
        reportLine(updated, lineKey);
        return updated;
      })
    );

    const isAtmp = selectedAcc?.standard_code === "ATMP";
    const hasParams = selectedAcc && getVisibleComponentParams(selectedAcc).length > 0;
    setExpandedKeys(prev => ({
      ...prev,
      [lineKey]: hasParams || isAtmp,
    }));
  };

  const updateComponentValue = (lineKey, componentCode, accessorialKey, value) => {
    setChargeLine(prev =>
      prev.map((line, idx) => {
        if (getLineKey(line, idx) !== lineKey) return line;

        const componentParams = [...(line.component_params || [])];
        const paramIndex = componentParams.findIndex(
          cp => cp.component_code === componentCode && cp.accessorial_key === accessorialKey
        );

        if (paramIndex >= 0) {
          componentParams[paramIndex] = {
            ...componentParams[paramIndex],
            accessorial_value: value,
          };
        } else {
          componentParams.push({
            component_code: componentCode,
            accessorial_key: accessorialKey,
            accessorial_value: value,
          });
        }

        const updated = { ...line, component_params: componentParams };
        reportLine(updated, lineKey);
        return updated;
      })
    );
  };

  // index >= 0 -> update/remove an existing attempt slot; index === -1 -> add a new attempt
  const updateAttemptedDriver = (lineKey, value, index) => {
    setChargeLine(prev =>
      prev.map((line, idx) => {
        if (getLineKey(line, idx) !== lineKey) return line;

        let attemptedDriverIds = [
          ...((line.driver_pay_params && line.driver_pay_params.attempted_driver_ids) || []),
        ];

        if (index >= 0) {
          if (!value) {
            attemptedDriverIds.splice(index, 1);
          } else {
            attemptedDriverIds[index] = value;
          }
        } else if (value) {
          attemptedDriverIds.push(value);
        }

        const updated = {
          ...line,
          driver_pay_params: {
            ...line.driver_pay_params,
            attempted_driver_ids: attemptedDriverIds,
          },
        };
        reportLine(updated, lineKey);
        return updated;
      })
    );
  };

  const getComponentValue = (line, componentCode, accessorialKey) =>
    (line.component_params || []).find(
      cp => cp.component_code === componentCode && cp.accessorial_key === accessorialKey
    )?.accessorial_value || "";

  const startAdd = () => {
    setChargeLine(prev => [...prev, newLineTemplate(orderDetails)]);
  };

  const handleDelete = (line, idx) => {
    const lineKey = getLineKey(line, idx);

    setChargeLine(prev => prev.filter((l, i) => getLineKey(l, i) !== lineKey));
    setExpandedKeys(prev => {
      const { [lineKey]: _removed, ...rest } = prev;
      return rest;
    });

    onDeleteAccessorialLine(lineKey, line, !!line.isNew);
  };

  const fetchManualBillingAccessorial = (currAcc, line, lineKey) => {
    const selectedAcc = accessorialList.find(acc => acc.accessorial_code === currAcc);

    const data = {
      organization_id: currentOrg.id || "",
      customer_order_id: orderDetails.customer_order_id || "",
      account_accessorial_id: selectedAcc?.id || "",
      location_id: line.location_id,
      accessorial_code: currAcc,
      account_id: orderDetails.account_id || "",
      source: "INVOICE",
    };

    saveManualBillingAccessorial(data).then(result => {
      if (result.success) {
        const cost = parseFloat(_.get(result, "accessorialcost", 0));

        setCurrentAccCost(cost);
        updateLineField(lineKey, "invoice_value", cost);
      } else {
        renderAlertMessage(result.errors);

        setCurrentAccCost(0);
        updateLineField(lineKey, "invoice_value", 0);
      }
    });
  };

  const FieldError = ({ message }) =>
    message ? <div className="charge-line__field-error">{message}</div> : null;

  const renderAttemptedDrivers = (line, lineKey) => {
    const attemptedDriverIds =
      (line.driver_pay_params && line.driver_pay_params.attempted_driver_ids) || [];
    // include a trailing empty slot so the user can always add "as many" drivers
    const slots = [...attemptedDriverIds, ""];

    return (
      <div className="charge-line__attempted-drivers">
        {slots.map((driverId, index) => {
          const isTrailingEmptySlot = index === slots.length - 1;
          const ordinalCount = index + 1;

          return (
            <div
              className="charge-line__attempted-driver-row"
              key={`${lineKey}-drv-${index}`}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 6,
                width: "100%",
              }}
            >
              <span
                className="charge-line__component-param-label"
                style={{ minWidth: 110, flexShrink: 0 }}
              >
                {getOrdinal(ordinalCount)}&nbsp;Attempt by
              </span>
              <span className="charge-line__component-param-sep" style={{ margin: "0 6px" }}>
                :
              </span>
              <DriverSelect
                showSearch
                data={drivers}
                value={driverId || undefined}
                onChange={value =>
                  updateAttemptedDriver(lineKey, value, isTrailingEmptySlot ? -1 : index)
                }
                placeholder={I18n.t("location.location")}
                style={{ width: 220 }}
                selectKey="id"
                selectValue="id"
                selectedValue="employee_code"
                size="small"
              />
              {!isTrailingEmptySlot && (
                <Tooltip title="Remove">
                  <Icon
                    type="delete"
                    className="charge-line__action-icon charge-line__action-icon--danger"
                    style={{ marginLeft: 8, cursor: "pointer" }}
                    onClick={() => updateAttemptedDriver(lineKey, "", index)}
                  />
                </Tooltip>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderChargeLine = (line, idx) => {
    const isNew = !!line.isNew;
    const lineKey = getLineKey(line, idx);
    const rowEditable = isNew || editable;
    const lineErrors = rowEditable ? getLineErrors(line) : {};
    const componentDefs = getLineComponentDefs(line);
    const hasAccessorialSelected = !!line.accessorial_code;
    const zeroAmount = isZeroInvValue(line);
    const hasComponentDefs = componentDefs.length > 0;
    const isAtmp = isAtmpLine(line);

    // Expansion applies to a NEW (unsaved) row when:
    // - it's an ATMP accessorial (always show driver attempts), or
    // - it has component params and a zero amount.
    const canExpand =
      isNew && hasAccessorialSelected && (isAtmp || (hasComponentDefs && zeroAmount));
    const isExpandedSectionVisible = canExpand && expandedKeys[lineKey];
    const showAmountInput = rowEditable && hasAccessorialSelected && !isExpandedSectionVisible;
    const hideAmountInput = line.isNew && hasAccessorialSelected && isExpandedSectionVisible;

    return (
      <div
        className={`charge-line-block${
          isExpandedSectionVisible ? " charge-line-block--expanded" : ""
        }`}
        key={lineKey}
      >
        <div className="charge-line" style={{ gridTemplateColumns }}>
          {/* <div className="charge-line__col charge-line__col--expand" /> */}

          <div className="charge-line__col charge-line__col--desc">
            {isNew ? (
              <>
                <Select
                  size="small"
                  showSearch
                  style={{ width: "100%" }}
                  placeholder="Select Accessorial"
                  value={line.accessorial_code || undefined}
                  // onChange={value => handleAccessorialSelect(lineKey, value)}
                  onChange={value => {
                    handleAccessorialSelect(lineKey, value);
                    fetchManualBillingAccessorial(value, line, lineKey);
                  }}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  className={lineErrors.accessorial_code ? "charge-line__select--error" : ""}
                >
                  {accessorialList
                    .filter(
                      acc =>
                        acc.accessorial_code === line.accessorial_code ||
                        !chargeLine.some(l => l.accessorial_code === acc.accessorial_code)
                    )
                    .map(acc => (
                      <Select.Option key={acc.id || acc._id} value={acc.accessorial_code}>
                        {acc.accessorial_name}
                      </Select.Option>
                    ))}
                </Select>
                <FieldError message={lineErrors.accessorial_code} />
              </>
            ) : (
              <Tooltip
                title={line.invoice_name}
                placement="topLeft"
                overlayStyle={{ maxWidth: "none" }}
                getPopupContainer={triggerNode => triggerNode.parentNode}
              >
                <div className="charge-line__desc-title charge-line__desc-title--truncate">
                  {line.invoice_name}
                  {line.item_description ? (
                    <span className="charge-line__desc-sub"> {line.item_description}</span>
                  ) : null}
                  {line.added_from !== "AUTO" && (
                    <sup style={{ color: "#f5222d", fontWeight: 600, fontSize: 11, marginLeft: 4 }}>
                      M
                    </sup>
                  )}
                </div>
              </Tooltip>
            )}
          </div>

          <div className="charge-line__col charge-line__col--code">
            <span className="charge-line__text-value">{line.accessorial_code || "-"}</span>
          </div>

          <div className="charge-line__col charge-line__col--type">
            {isNew ? (
              <>
                <Select
                  size="small"
                  style={{ width: "100%" }}
                  value={line.location_id || undefined}
                  onChange={value => updateLineField(lineKey, "location_id", value)}
                  placeholder="Order Type"
                  className={lineErrors.location_id ? "charge-line__select--error" : ""}
                  dropdownMatchSelectWidth={false}
                >
                  {(orderDetails.all_locations || []).map(loc => (
                    <Select.Option key={loc.location_id} value={loc.location_id}>
                      {loc.order_type}
                    </Select.Option>
                  ))}
                </Select>
                <FieldError message={lineErrors.location_id} />
              </>
            ) : (
              (line.order_type || line.location_type) && (
                <Tag
                  className={`charge-line__type-tag charge-line__type-tag--${(
                    line.order_type || line.location_type
                  ).toLowerCase()}`}
                >
                  {line.order_type || line.location_type}
                </Tag>
              )
            )}
          </div>

          <div className="charge-line__col charge-line__col--gl">
            {rowEditable ? (
              <>
                <GLCodeSelect
                  invoiceType="accessorial_charges"
                  defaultValue={!groupGlByWarehouseSetting ? line?.gl_code : ""}
                  data={glCodesList || []}
                  value={line.gl_code}
                  onChange={value => updateLineField(lineKey, "gl_code", value)}
                  selectKey={"gl_code"}
                  selectValue={"gl_code"}
                  selectedValue={"gl_code"}
                  selectedCode={"description"}
                  style={{ width: 120 }}
                  showSearch={true}
                  placeholder={I18n.t("invoices.gl_code")}
                  dropdownMatchSelectWidth={false}
                  size="small"
                  toDisable={false}
                  currentAccessorialHasGlCode={false}
                  className={lineErrors.gl_code ? "charge-line__select--error" : ""}
                />{" "}
                <FieldError message={lineErrors.gl_code} />
              </>
            ) : (
              line.gl_code || "-"
            )}
          </div>

          <div className="charge-line__col charge-line__col--amount">
            {hideAmountInput ? null : showAmountInput ? (
              <>
                <Input
                  size="small"
                  type="number"
                  prefix="$"
                  value={line.invoice_value}
                  className={lineErrors.invoice_value ? "charge-line__select--error" : ""}
                  onChange={e => updateLineField(lineKey, "invoice_value", e.target.value)}
                />{" "}
                <FieldError message={lineErrors.invoice_value} />
              </>
            ) : (
              <span className="charge-line__text-value">
                ${Number(line.invoice_value || 0).toFixed(2)}
              </span>
            )}
          </div>

          {editable && (
            <div className="charge-line__col charge-line__col--actions">
              <Tooltip title="Remove">
                <Icon
                  type="delete"
                  className="charge-line__action-icon charge-line__action-icon--danger"
                  onClick={() => handleDelete(line, idx)}
                />
              </Tooltip>
            </div>
          )}
        </div>

        {isExpandedSectionVisible && (
          <div className="charge-line__component-params">
            {isAtmp && renderAttemptedDrivers(line, lineKey)}

            {componentDefs.map(param => (
              <div className="charge-line__component-param" key={`${lineKey}-${param.key}`}>
                <span className="charge-line__component-param-label">{param.name}</span>
                <span className="charge-line__component-param-sep">:</span>
                <Input
                  size="small"
                  value={getComponentValue(line, param.component_code, param.key)}
                  onChange={e =>
                    updateComponentValue(lineKey, param.component_code, param.key, e.target.value)
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const hasNewLineInProgress = chargeLine.some(l => l.isNew);
  const hasEmptyFields = hasIncompleteLine(chargeLine, editable);

  useEffect(() => {
    onValidationChange(section.key, hasEmptyFields);
  }, [section.key, chargeLine, editable]);

  return (
    <div className="charge-section accessorial-charge-section">
      <div className="charge-section__header">
        <span className="charge-section__title">{section.title}</span>
        <span className="charge-section__lines-badge">
          {chargeLine.length} {chargeLine.length === 1 ? "line" : "lines"}
        </span>
        <span className="charge-section__subtotal">Subtotal ${section.subtotal.toFixed(2)}</span>
      </div>

      <div
        className={`charge-section__table${editable ? "" : " charge-section__table--view-only"}`}
      >
        <div className="charge-line charge-line--head" style={{ gridTemplateColumns }}>
          {/* <div className="charge-line__col charge-line__col--expand" /> */}
          <div className="charge-line__col charge-line__col--desc"># DESCRIPTION</div>
          <div className="charge-line__col charge-line__col--code">CODE</div>
          <div className="charge-line__col charge-line__col--type">ORDER TYPE</div>
          <div className="charge-line__col charge-line__col--gl">GL CODE</div>
          <div className="charge-line__col charge-line__col--amount">AMOUNT</div>
          {editable && <div className="charge-line__col charge-line__col--actions" />}
        </div>
        {chargeLine.map(renderChargeLine)}
      </div>

      {editable && (
        <>
          <Button
            className="charge-section__add-btn"
            icon="plus"
            onClick={startAdd}
            disabled={hasEmptyFields}
          >
            Add Accessorial Line
          </Button>

          {hasEmptyFields && (
            <div className="charge-section__error-text">
              Please fill in all required fields before adding a new line or saving.
            </div>
          )}
        </>
      )}
    </div>
  );
};

AccessorialChargeSection.propTypes = {
  section: PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.string,
    subtotal: PropTypes.number,
    lines: PropTypes.array,
  }).isRequired,
  onAddAccessorialLine: PropTypes.func,
  onEditAccessorialLine: PropTypes.func,
  onDeleteAccessorialLine: PropTypes.func,
  editable: PropTypes.bool,
  glCodesList: PropTypes.array,
  orderDetails: PropTypes.object,
  groupGlByWarehouseSetting: PropTypes.bool,
  accessorialList: PropTypes.array,
  displayComponents: PropTypes.array,
  orgComponents: PropTypes.array,
  currentOrg: PropTypes.object,
  drivers: PropTypes.array,
};

export default AccessorialChargeSection;
